<template>
  <section class="ma-4 ma-md-8">
    <t-page-title title="Promoters | Credenciar" />
    <v-row justify="center" align="center">
      <v-col>
        <data-table
          :headers="headers"
          :provider="fetch"
          :filters="filters"
          :resource="resource"
        >
          <template #[`item.dates`]="{ item }">
            <span> {{ item.dates.open }}</span>
          </template>
          <template #[`item.id`]="{ item }">
            <span> {{ item.id.split("-")[0] }}</span>
          </template>
          <template #[`item.link`]="{ item }">
            <v-card
              readonly
              elevation="0"
              @click="
                copy(`${url}/ticket/buy/${eventFilter.model.value}?rel=${item.tag}`)
              "
            >
              {{ `${url}/ticket/buy/${eventFilter.model.value}?rel=${item.tag}` }}
            </v-card>
          </template>
          <template #[`item.actions`]="{ item }">
            <v-checkbox-btn
              v-model="item.is_linked"
              inline
              color="primary"
              @click="toggle(item)"
            />
          </template>
        </data-table>
      </v-col>
    </v-row>
  </section>
</template>

<script lang="ts" setup>
import { ref, type Ref } from 'vue'
import type { AxiosResponse } from 'axios'
import type { DataTableHeader, DataTableFilter } from '@/types/DataTable'
import DataTable from '@/components/datatable/DataTable.vue'
import TPageTitle from '@/components/TPageTitle.vue'
import AffiliateService from '@/services/AffiliateService'
import type Affiliate from '@/types/Affiliate'
import type PaginatedResponse from '@/types/PaginatedResponse'
import { useTextFilter } from '@/helpers/reports/textFilter'
import { useEventFilter } from '@/helpers/reports/eventFilter'
import { useProducerStore } from '@/stores/producer'
import { useSnackBar } from '@/stores/snackbar'

// AffiliateService.accreditations() actually responds with these extra fields
// (used by the template below), but the shared Affiliate type doesn't declare
// them; TServersideDatatable never caught this because it typed items as
// `unknown[]`. Narrowly extended here rather than widening the shared type
// based on a single endpoint's response.
interface AccreditationAffiliate extends Affiliate {
  dates: { open: string }
  is_linked: boolean
}

const snackbar = useSnackBar()

const resource = 'affiliates'

const headers: DataTableHeader[] = [
  { title: 'ID', value: 'id', key: 'id', sortable: false },
  { title: 'Nome', value: 'name', key: 'name', sortable: false },
  { title: 'Link', value: 'link', key: 'link', sortable: false },
  {
    title: 'Credenciar',
    value: 'actions',
    key: 'actions',
    sortable: false,
    align: 'center',
  },
]

const eventFilter = useEventFilter({
  producer: useProducerStore().producer!,
  label: 'Evento',
  field: 'evento',
  type: 'autocomplete',
})

const filters: Ref<DataTableFilter[]> = ref([
  useTextFilter({
    label: 'Nome',
    field: 'nome',
    icon: 'fa-regular fa-signature',
    required: false,
  }),
  eventFilter,
])

const url = import.meta.env.VITE_SALES_URL

async function fetch(page: number, filtersString: string, per_page?: number): Promise<AxiosResponse<PaginatedResponse<AccreditationAffiliate>>> {
  try {
    return await new AffiliateService().accreditations(
      page,
      filtersString,
      per_page,
      eventFilter.model.value as string,
    ) as unknown as AxiosResponse<PaginatedResponse<AccreditationAffiliate>>
  } catch (error) {
    snackbar.show('Não foi possível buscar os promoters', 'error')
    throw error
  }
}

async function toggle(model: Affiliate) {
  try {
    const response = await new AffiliateService().toggle(model, eventFilter.model.value as string)

    snackbar.show(response.data.message, 'success')
  } catch (error) {
    snackbar.show('Não foi possível habilitar o promoter', 'error')
    throw error
  }
}

function copy(text: string) {
  navigator.clipboard.writeText(text)

  snackbar.show('Texto copiado', 'success')
}
</script>
